import {
  ContactFragmentFragment,
  CurrentProfileFragmentFragment,
  TargetModel,
  useAuthLogin,
  UserQuery,
  useUser
} from '@lopo-tech/lopo-api'
import React, { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch } from 'redux-thunk'
import { IAction } from 'src/actions/'
import { useChangeAppLocation } from 'src/actions/app-state/app-state_hooks'
import {
  IUserMeRequestErrorAction,
  IUserMeRequestSuccessAction,
  USER_ME_REQUEST_ERROR,
  USER_ME_REQUEST_SUCCESS
} from 'src/actions/user/user'
import { useAuthContext } from 'src/context/authContext'
import {
  IContact,
  IProfiles,
  IUser,
  IUserProfile,
  IUserProfileType
} from 'src/interfaces/user'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { IRootState } from 'src/reducers/interface'
import { PROFILE_SWITCH_RESET } from 'src/reducers/index'
import { AuthenticationHelper } from 'src/utils/authentication/authentication'
import { StoredProfileHelper } from '../utils/location_storage/location_storage'

interface UserDataProviderProps {
  children: React.ReactNode;
}

type BlogProfile = UserQuery['me']['blogProfiles'][number];
type PressProfile = UserQuery['me']['pressProfiles'][number];
type AuthorityProfile = UserQuery['me']['authorityProfiles'][number];

// TODO: clean up once it is required in the api, or clean up code that uses it
const mapContact = (
  contact?: ContactFragmentFragment | null
): IContact | undefined => {
  if (!contact) {
    return undefined
  }

  return {
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    phone: contact.phone
  }
}

const mapTargetModel = (
  profile: CurrentProfileFragmentFragment
): IUserProfileType | undefined => {
  if (profile.__typename !== 'IntermediateProfile' || !profile.targetModel) {
    return undefined
  }

  switch (profile.targetModel) {
    case TargetModel.BlogProfile:
      return 'BlogProfile'
    case TargetModel.PrivateProfile:
      return 'PrivateProfile'
    default:
      return undefined
  }
}

const transformCurrentProfile = (
  profile: CurrentProfileFragmentFragment
): IUserProfile => {
  return {
    ...profile,
    type: profile.__typename,
    targetModel: mapTargetModel(profile),
    contact:
      profile.__typename === 'AuthorityProfile' ||
      profile.__typename === 'BlogProfile' ||
      profile.__typename === 'PressProfile'
        ? mapContact(profile.contact)
        : undefined,
    isMessengerEnabled: false // TODO: reimplement once gql supports messenger
  }
}

const transformPublicProfile = (
  profile: AuthorityProfile | BlogProfile | PressProfile
): IUserProfile => {
  return {
    id: profile.id,
    name: profile.name,
    type: profile.__typename,
    avatar: profile.avatar,
    isMessengerEnabled: false
  }
}

const useSetLocationShapeOnLogin = (
  currentProfile: CurrentProfileFragmentFragment | null | undefined
) => {
  const currentLocationShape = useGetAppLocationShape()
  const changeAppLocation = useChangeAppLocation()

  useEffect(() => {
    if (!currentLocationShape && currentProfile?.address.locationShape) {
      changeAppLocation(currentProfile.address.locationShape)
    }
  }, [currentProfile, currentLocationShape, changeAppLocation])
}

// TODO: Refactor this once the whole application is migrated to GraphQL and local storage is not required for tokens anymore
export const UserDataProvider: React.FC<UserDataProviderProps> = ({
  children
}) => {
  const { selectedProfileId, setSelectedProfileId } = useAuthContext()
  const dispatch = useDispatch<ThunkDispatch<IRootState, unknown, IAction>>()
  const { isAuthenticated, setTokens } = useAuthLogin()
  const tokens = AuthenticationHelper.parseStoredOAuth2Credentials()
  const hasValidTokens =
    tokens && AuthenticationHelper.checkTokenValidity(tokens)

  // Reset profile-specific Redux state when selectedProfileId changes (but not on initial mount)
  const prevProfileIdRef = useRef(selectedProfileId)
  useEffect(() => {
    if (prevProfileIdRef.current !== null && prevProfileIdRef.current !== selectedProfileId && selectedProfileId !== null) {
      dispatch({ type: PROFILE_SWITCH_RESET })
    }
    prevProfileIdRef.current = selectedProfileId
  }, [selectedProfileId, dispatch])

  // Use tokens to determine if we should fetch user data, even if isAuthenticated is false
  const shouldFetchUserData = isAuthenticated || hasValidTokens
  const { data, error, loading } = useUser({ skip: !shouldFetchUserData })

  useSetLocationShapeOnLogin(data?.currentProfile)

  useEffect(() => {
    if (hasValidTokens && !isAuthenticated) {
      setTokens({
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
        created_at: tokens.createdAt,
        expires_in: tokens.expiresIn
      })
      Promise.resolve().then(() => {
        setSelectedProfileId(StoredProfileHelper.getCurrentProfileId())
      })
    }
  }, [isAuthenticated, hasValidTokens, tokens, setTokens])

  useEffect(() => {
    if (data?.currentProfile && !loading) {
      const userData = data.me
      const currentProfile = data.currentProfile

      const isValidSelectedProfileId =
        selectedProfileId &&
        (userData.privateProfile?.id === selectedProfileId ||
          userData.blogProfiles?.some(
            (profile) => profile.id === selectedProfileId
          ) ||
          userData.pressProfiles?.some(
            (profile) => profile.id === selectedProfileId
          ) ||
          userData.authorityProfiles?.some(
            (profile) => profile.id === selectedProfileId
          ))

      if (!isValidSelectedProfileId) {
        setSelectedProfileId(currentProfile.id)
      }

      const profiles: IProfiles = {}

      // Add current profile with full details
      profiles[currentProfile.id] = transformCurrentProfile(currentProfile)

      // Add other profiles with basic info only (for sidebar display)
      if (
        userData.privateProfile &&
        currentProfile.id !== userData.privateProfile.id
      ) {
        profiles[userData.privateProfile.id] = {
          id: userData.privateProfile.id,
          name: userData.privateProfile.name,
          isMessengerEnabled: false,
          type: userData.privateProfile.__typename
        }
      }

      userData.blogProfiles?.forEach((profile: BlogProfile) => {
        if (currentProfile.id !== profile.id) {
          profiles[profile.id] = transformPublicProfile(profile)
        }
      })

      userData.pressProfiles?.forEach((profile: PressProfile) => {
        if (currentProfile.id !== profile.id) {
          profiles[profile.id] = transformPublicProfile(profile)
        }
      })

      userData.authorityProfiles?.forEach((profile: AuthorityProfile) => {
        if (currentProfile.id !== profile.id) {
          profiles[profile.id] = transformPublicProfile(profile)
        }
      })

      if (Object.keys(profiles).length === 0) {
        profiles.no_user_profile = {
          isMessengerEnabled: false,
          type: 'no_user_profile'
        }
      }

      const transformedUserData: IUser = {
        confirmedAt: userData.confirmedAt,
        email: userData.email,
        identities: [],
        hasToken: true,
        loggedIn: true,
        profiles,
        selectedProfileId: currentProfile.id,
        phone: undefined,
        unverifiedPhone: userData.unverifiedPhone,
        remainingPhoneVerificationAttempts: userData.remainingVerificationAttempts,
        verifiedAt: userData.verifiedAt,
        receiveNewsletter: userData.receiveNewsletter ?? undefined,
        loginRequestPending: false,
        unconfirmedEmail: userData.unconfirmedEmail
      }

      const successAction: IUserMeRequestSuccessAction = {
        type: USER_ME_REQUEST_SUCCESS,
        payload: transformedUserData
      }
      dispatch(successAction)
    }
  }, [data, loading, dispatch])

  useEffect(() => {
    if (error && !loading) {
      const errorAction: IUserMeRequestErrorAction = {
        type: USER_ME_REQUEST_ERROR
      }
      dispatch(errorAction)
    }
  }, [error, loading, dispatch])

  return <>{children}</>
}
